import { Component, inject, signal, type Signal, type WritableSignal } from "@angular/core";
import { Router } from "@angular/router";

import { AUTH_PROVIDER } from "../../../core/auth/auth-provider.token";
import { TENANT_CONTEXT } from "../../../core/auth/tenant-context.token";
import { AuthStateService } from "../../../core/services/auth-state.service";
import { BrandingService } from "../../../core/services/branding.service";
import { AuthLayoutComponent } from "../../../shared/components/auth-layout/auth-layout.component";
import {
  MicrosoftMarkComponent,
} from "../../../shared/components/microsoft-mark/microsoft-mark.component";
import { BrandLogoComponent } from "../../../shared/components/brand-logo/brand-logo.component";

import type { IPortalBranding } from "../../../core/models/i-portal-branding";
import type { ITenantContext } from "../../../core/auth/i-tenant-context";

/**
 * The unauthenticated entry point, and it behaves differently per host.
 *
 * On the APEX host this is the Davaco staff door — board 7e. A real screen with
 * a "Continue with Microsoft" button the visitor clicks. Staff arrive here
 * directly and deliberately, and the door is where the help routes and the
 * "Davaco staff only" statement live.
 *
 * On a CUSTOMER subdomain it stays the redirect interstitial — boards 2c/2d.
 * The customer's actual login screen is Auth0's Universal Login, branded to
 * match the portal (infrastructure/auth0/universal-login), so a door here
 * would be a second screen in front of the real one, asking a customer to
 * click through a page that says nothing their own branded login will not say
 * better.
 */
@Component({
  selector: "app-login",
  standalone: true,
  imports: [AuthLayoutComponent, MicrosoftMarkComponent, BrandLogoComponent],
  templateUrl: "./login.component.html",
  styleUrl: "./login.component.scss",
})
export class LoginComponent {

  private readonly provider = inject(AUTH_PROVIDER);

  private readonly brandingService = inject(BrandingService);

  private readonly authState = inject(AuthStateService);

  private readonly tenant: ITenantContext = inject(TENANT_CONTEXT);

  private readonly router = inject(Router);

  public readonly branding: Signal<IPortalBranding> = this.brandingService.branding;

  /** True on the apex host: Davaco staff, authenticating against Entra. */
  public readonly isStaffDoor: boolean = this.tenant.kind === "entra";

  /**
   * A customer host whose slug has no Auth0 organization behind it — mistyped,
   * never provisioned, or outlived its organization.
   *
   * The branding endpoint is what reveals it: an unknown slug is answered with
   * the Davaco defaults, which carry a null `organizationId`, so this is
   * readable BEFORE any token exists.
   *
   * All three halves of the test matter. The apex host also has a null
   * organization id and is not an error there. And the branding has to have
   * actually RESOLVED — the same null arrives when the endpoint could not be
   * reached, which says nothing about whether the organization exists, and
   * treating it as an unknown slug would strand a customer on the fallback over
   * a transient failure. An unresolved load falls through to the redirect,
   * which fails loudly and retryably instead.
   */
  public readonly isUnknownOrganization: boolean =
    this.tenant.kind === "auth0" &&
    this.brandingService.resolved() &&
    this.branding()
.organizationId === null;

  public readonly failed: WritableSignal<boolean> = signal<boolean>(false);

  /** Set while the hand-off is in flight, so the button cannot be double-fired. */
  public readonly pending: WritableSignal<boolean> = signal<boolean>(false);

  constructor() {
    // An already-signed-in visitor must NEVER be sent back to the identity
    // provider from here, or the portal never finishes signing anyone in.
    //
    // The loop: an unauthenticated visit to "/" is bounced to "/login" by
    // authGuard, so "/login" is the URL that initiates the redirect. MSAL's
    // navigateToLoginRequestUrl (on by default) then returns the browser to
    // that same URL once it has redeemed the code — with a complete session in
    // hand — and this constructor immediately started a NEW login. Entra
    // eventually refuses the repeated /authorize with "We couldn't sign you in.
    // Please try again.", which reads like a credential problem and is not one.
    if (this.authState.isAuthenticated()) {
      void this.router.navigate(["/"]);
      return;
    }

    // A customer host with no organization behind it cannot complete a login,
    // and the provider would refuse to start one. Send it to the fallback
    // (board 7f right) instead of letting the redirect fail into "something
    // went wrong … this is usually temporary", which is wrong twice: nothing
    // went wrong, and waiting does not fix an unprovisioned slug.
    if (this.isUnknownOrganization) {
      void this.router.navigate(["/unknown-organization"]);
      return;
    }

    // Auth0 refused the last attempt. A membership refusal has its own screen;
    // anything else shows the retry state. Either way, never auto-redirect:
    // Auth0 would refuse the same account again, and the visitor would bounce
    // between here and Auth0 without ever being told why.
    const refusal = this.authState.refusal();
    if (refusal?.kind === "not-a-member") {
      void this.router.navigate(["/no-access"]);
      return;
    }
    if (refusal !== null) {
      this.failed.set(true);
      return;
    }

    // Customers are handed straight to their own branded Universal Login; only
    // staff get a door to click.
    if (!this.isStaffDoor) {
      void this.redirect();
    }
  }

  public async redirect(): Promise<void> {
    this.failed.set(false);
    this.pending.set(true);

    try {
      await this.provider.login();
    } catch {
      this.failed.set(true);
    } finally {
      // A successful login navigates away, so this only matters on the failure
      // path — but leaving the button disabled after a failure would strand the
      // visitor on a screen whose only action no longer works.
      this.pending.set(false);
    }
  }
}
